import { AxiosError } from 'axios';
import { action, makeAutoObservable, observable, computed } from 'mobx';
import { makePersistable } from 'mobx-persist-store';
import { toast } from 'react-toastify';
import { TFunction } from 'i18next';
import { v4 as uuidv4 } from 'uuid';
import { format } from 'date-fns';
import JSONBig from 'json-bigint';
import { AbortRequestsStore } from '../interfaces';
import ViewStore from '../ViewStore';
import { SignInResponse } from '../LoginStore/Payloads/responses';
import { IndexDocumentRequest, TelemetryLogsRequest } from './Payloads/requests';
import {
  ActionResponse,
  GetDeclarationContextResponse,
  GetFileByRemoteAPIResponse,
  GetIndexDocumentResponse,
  GetRunDeclarationViewerResponse,
  OMSReverseGeocodingResponse,
  SaveInstrumentationResponse,
  SynchronizeDeclarationResponse,
} from './Payloads/responses';
import {
  API_POST_HISTORIQUEDECLARATION,
  API_POST_QC_ACTION,
  API_GET_FORM_CONTEXT_DEEP_LINK,
  API_GET_FORM_CONTEXT_DEEP_LINK_WITHOUT_EXCHANGE,
} from '40.quickConnect.DataAccess/axios/apiRoutes';
import { QcRequestConfig } from '40.quickConnect.DataAccess/axios/types';
import { getDisplayableErrorMessage } from '40.quickConnect.DataAccess/axios/httpErrorMessage';
import {
  FormEditedData,
  FieldDesc,
  DeclarationViewer,
  KeyValuePair,
  DocumentIndexDTO,
  AttachmentItemData,
  QCAttachment,
  FieldData,
  convertFromFormEditedData,
  ItemData,
  DeclarationContext,
  Position,
  StepDesc,
  LocalBackupDeclaration,
  FormEditedDataDTO,
  TaskEditedDataType,
  QCErrorHTTP,
  NotificationDesc,
  QCNotification,
  EditDesc,
  NotificationTarget,
} from '90.quickConnect.Models/models';
import {
  API_GET_DECLARATIONS,
  API_GET_DOCUMENT_INDEX,
  API_SYNCHRONIZE_DECLARATION,
} from '40.quickConnect.DataAccess/axios/apiRoutes';
import {
  getDeviceType,
  flatten,
  errorHandler,
  getDeclarationEditLockName,
  getFormEditLockName,
  getHeldEditLockNamesAsync,
  runWithEditLockAsync,
} from '80.quickConnect.Core/helpers';
import {
  mapToFieldData,
  mapDeclarationViewer,
  mapDeclarationContext,
  mapToFieldVisibility,
  mapFromQCAttachmentToAttachmentItemData,
  mapDistantUriForOtherStep,
} from '90.quickConnect.Models/mappings';
import CustomLogger from '80.quickConnect.Core/logger/customLogger';
import declarationsDb from '40.quickConnect.DataAccess/indexedDb/dbs/declarationsDb';
import { APP_NAME, APP_VERSION, FormLibVersion } from 'const';
import getSessionId from '80.quickConnect.Core/helpers/getSessionId';
import { IsAnAttachmentField } from '90.quickConnect.Models/models/fields/fieldDesc/fieldDesc';
import attachmentDb from '40.quickConnect.DataAccess/indexedDb/dbs/attachmentDb';
import {
  DocumentTransferState,
  FieldType,
  FormType,
  InboxItemType,
  InternalFieldState,
  WorkflowStatus,
  WorkflowStepStatus,
  DeclarationSynchronizationStatus,
} from '90.quickConnect.Models/enums';
import logDb from '40.quickConnect.DataAccess/indexedDb/dbs/logDb';
import { IFieldSearchHistory, fieldSearchHistoryDb } from '40.quickConnect.DataAccess/indexedDb/dbs/historyDb';
import {
  RequestData,
  SearchRequestData,
} from '10.quickConnect.app/components/domain/Home/Declarations/DeclarationHistory/types';
import RootStore from '30.quickConnect.Stores/RootStore';
import { AddressData } from '90.quickConnect.Models/models/fields/values/addressData';
import { OMS_GET_REVERSE_GEOCODING_URI } from '40.quickConnect.DataAccess/axios/openMapStreetApiRoutes';
import buildApiUrl from '40.quickConnect.DataAccess/axios/buildApiUrl';
import { SynchronizeDeclaration } from '40.quickConnect.DataAccess/indexedDb/dbs/queriesTypes/declaration';
import IClientHTTP from '40.quickConnect.DataAccess/ClientHTTP/interface';
import {
  buildCalculatedLabelWithEquipmentTokens,
  buildCalculatedLabelWithFieldTokens,
  buildCalculatedLabelWithSystemRefTokens,
} from '30.quickConnect.Stores/helpers/getValueForCalculateLabel';
import { dateFormatWithMillisecond } from '80.quickConnect.Core/helpers/dateFormat';
import { StringExtension } from '80.quickConnect.Core/formatting/StringExtension';
import DataSourceValue from '30.quickConnect.Stores/helpers/DataSourceValue';
import { isATodoListData, isAnAttachmentItemsData, isFieldDataArray } from '90.quickConnect.Models/guards';
import inboxesDb from '40.quickConnect.DataAccess/indexedDb/dbs/inboxesDb';
import getLRUFullId from '70.quickConnect.Common/LRU';
import { notificationFieldsCheckUnprocessedData } from '50.quickConnect.Fields/FieldsTypes/Inputs/NotificationQcField/helpers';
import { ValidationActionType } from '90.quickConnect.Models/enums/declarations/ValidationActionType';

/** État de la saisie affichée */
export type CurrentEntry = {
  // Modifiable dans cet onglet (sinon consultée : déclaration envoyée, ou saisie ouverte dans un autre onglet)
  isEditable: boolean;
  // Modifications pas encore enregistrées en brouillon ni envoyées
  isModified: boolean;
  // Commencée avant cette ouverture (brouillon, saisie interrompue, élément de la boîte de réception)
  isStarted: boolean;
};

class DeclarationStore implements AbortRequestsStore {
  // Tag
  private static readonly TAG = '30.quickConnect.Stores/RootStore/DeclarationStore/index.ts';

  /** Nombre d'echecs consecutifs de l'envoi des logs d'instrumentation avant de tracer une erreur. */
  private static readonly MAX_INSTRUMENTATION_ATTEMPTS = 3;

  /** Delai avant le rejeu differe de l'envoi des logs d'instrumentation (5 minutes). */
  private static readonly INSTRUMENTATION_RETRY_DELAY_IN_MS = 5 * 60 * 1000;

  /** Etats pour lesquels une synchronisation est en cours: on ne rejoue pas l'instrumentation. */
  private static readonly SYNCHRONIZATION_IN_PROGRESS_STATUSES: DeclarationSynchronizationStatus[] = [
    DeclarationSynchronizationStatus.IndexingAttachments,
    DeclarationSynchronizationStatus.SynchronizingDeclaration,
    DeclarationSynchronizationStatus.UploadingAttachments,
  ];

  /** Nombre d'echecs consecutifs de l'envoi des logs d'instrumentation, conserve entre les sessions. */
  instrumentationFailureCount = 0;

  /** Verrou empechant deux envois simultanes des logs d'instrumentation. */
  private isSendingInstrumentationLogs = false;

  /** Identifiant du minuteur portant le rejeu differe de l'envoi des logs d'instrumentation. */
  private instrumentationRetryTimeoutId: ReturnType<typeof setTimeout> | undefined;

  /** Ecouteur du retour de connexion reseau, pose uniquement lorsqu'un rejeu est en attente. */
  private onNetworkBackOnline: (() => void) | undefined;

  sendingDeclaration = false;

  clientHTTP: IClientHTTP;

  // Ne pas mettre dans resetStore
  shouldAbortRequests = false;

  // Saisie affichée dans cet onglet, ouverte et fermée par la page de la déclaration (undefined hors d'une saisie).
  // Non persistée : une saisie rechargée est reprise depuis sa sauvegarde automatique
  currentEntry: CurrentEntry | undefined = undefined;

  logger: CustomLogger;

  HistoryStore: ViewStore<DeclarationViewer>;

  DraftsStore: ViewStore<DeclarationViewer>;

  LeftMenuHistoryStore: ViewStore<DeclarationViewer>;

  LeftMenuDraftsStore: ViewStore<DeclarationViewer>;

  requiredField: FieldDesc | undefined = undefined;

  scrollTarget: React.MutableRefObject<HTMLElement | undefined> | undefined;

  verifRequiredField = false;

  RootStore: RootStore;

  isDeletedDeclarationContext = false;

  // Message affichable de la dernière erreur de récupération du contexte d'un formulaire ou d'une déclaration
  contextRetrievalErrorMessage: string | undefined = undefined;

  // Flag d'ouverture de la Dialogue de déclaration afin d'enregistrer ou non
  openDeclarationDialog = false;

  // Flag pour savoir si le tiroir est ouvert ou pas
  isDrawerOpened = false;

  currentStepWorkFlow: StepDesc | undefined;

  numberDisplayed = 0;

  openConsentPopup = false;

  isMaxAttachment = false;

  // Saisies interrompues (sauvegardées automatiquement, ouvertes dans aucun onglet) à proposer de reprendre
  backupDclContext: LocalBackupDeclaration[] = [];

  // QCScriptObj
  controllerMethod = '';

  qcsFieldId = '';

  declarationId = '';

  sendAPI = false;

  // Indication pour les pièces jointes
  attachmentsToSend = 0;

  sendingAttachments = false;

  synchronizationStatus: DeclarationSynchronizationStatus = DeclarationSynchronizationStatus.NotStarted;

  synchronizationError: string | undefined;

  private synchronizationPromise: Promise<boolean> | undefined;

  // Deep Link
  isDeepLink = false;

  // InternalData
  internalDataDico: Map<string, FieldData> = new Map<string, FieldData>();

  // focusField
  fieldFocused: string | undefined;

  // current scope
  currentBaseScope: string | undefined;

  // url to upload images in Image
  urlToUseForImagesBank: string | undefined;

  constructor(rootStore: RootStore, logger: CustomLogger) {
    this.RootStore = rootStore;
    this.clientHTTP = rootStore.clientHTTP;
    this.logger = logger;

    // Les trois champs techniques du rejeu differe de l'instrumentation sont prives: TypeScript
    // les exclut de `keyof this`, donc de la map d'annotations. Ils sont declares ici en cles
    // additionnelles pour pouvoir etre annotes `false` (etat interne, non observable).
    makeAutoObservable<
      DeclarationStore,
      'isSendingInstrumentationLogs' | 'instrumentationRetryTimeoutId' | 'onNetworkBackOnline'
    >(
      this,
      {
        sendingDeclaration: observable,
        shouldAbortRequests: observable,
        numberDisplayed: observable,
        currentEntry: observable.ref,
        openDeclarationDialog: observable,
        isDrawerOpened: observable,
        openConsentPopup: observable,
        isDeletedDeclarationContext: observable,
        contextRetrievalErrorMessage: observable,
        currentStepWorkFlow: observable,
        isMaxAttachment: observable,
        declarationId: observable,
        sendAPI: observable,
        requiredField: observable,
        scrollTarget: observable,
        verifRequiredField: observable,
        attachmentsToSend: observable,
        sendingAttachments: observable,
        synchronizationStatus: observable,
        synchronizationError: observable,
        isDeepLink: observable,
        internalDataDico: observable,
        internalData: computed,
        isEntryEditable: computed,
        hasUnsavedChanges: computed,
        mustConfirmDeclarationExit: computed,
        fieldFocused: observable,
        currentBaseScope: observable,
        urlToUseForImagesBank: observable,
        instrumentationFailureCount: observable,
        // Etat technique interne du rejeu differe: inutile de l'exposer comme observable.
        isSendingInstrumentationLogs: false,
        instrumentationRetryTimeoutId: false,
        onNetworkBackOnline: false,

        setInstrumentationFailureCount: action.bound,
        setFieldFocused: action.bound,
        setCurrentBaseScope: action.bound,
        setIsDeepLink: action.bound,
        setAttachmentsToSend: action.bound,
        setSendingAttachments: action.bound,
        setSynchronizationStatus: action.bound,
        setSynchronizationError: action.bound,
        setBackupDclContext: action.bound,
        setSendAPI: action.bound,
        setDeclarationStoreId: action.bound,
        openEntry: action.bound,
        markEntryModified: action.bound,
        closeEntry: action.bound,
        openDialog: action.bound,
        closeDialog: action.bound,
        setOpenDrawer: action.bound,
        setCloseDrawer: action.bound,
        getDeclarationContext: action.bound,
        setShouldAbortRequests: action,
        setSendingDeclaration: action.bound,
        saveDeclarationAsync: action.bound,
        getDeclarationList: action.bound,
        removeLocalDeclaration: action.bound,
        getFileByRemoteAPI: action.bound,
        setCloseConsentPopup: action.bound,
        setOpenConsentPopup: action.bound,
        setIsDeletedDeclarationContext: action.bound,
        setContextRetrievalErrorMessage: action.bound,
        setCurrentStepWorkFlow: action.bound,
        setIsMaxAttachment: action.bound,
        setRequiredField: action.bound,
        setScrollTarget: action.bound,
        setVerifRequiredField: action.bound,
        findBackUpsByUserUPNAsync: action.bound,
        setUrlToUseForImagesBank: action.bound,
      },
      { autoBind: true },
    );

    void makePersistable(this, {
      name: 'DeclarationStore',
      properties: ['urlToUseForImagesBank', 'instrumentationFailureCount'],
      storage: window.localStorage,
    });

    this.HistoryStore = new ViewStore<DeclarationViewer>(this.logger, 'HistoryStore');
    this.DraftsStore = new ViewStore<DeclarationViewer>(this.logger, 'DraftsStore');

    this.LeftMenuHistoryStore = new ViewStore<DeclarationViewer>(this.logger, 'LeftMenuHistoryStore');
    this.LeftMenuDraftsStore = new ViewStore<DeclarationViewer>(this.logger, 'LeftMenuDraftsStore');
  }

  get internalData(): FieldData[] {
    if (this.internalDataDico.size === 0) return [];
    return Array.from(this.internalDataDico.values());
  }

  // Saisie modifiable dans cet onglet ; elle est enregistrée dans l'UO où elle a été ouverte, qui ne peut donc pas changer
  get isEntryEditable(): boolean {
    return !!this.currentEntry?.isEditable;
  }

  // Modifications à sauvegarder automatiquement
  get hasUnsavedChanges(): boolean {
    return this.isEntryEditable && !!this.currentEntry?.isModified;
  }

  // Quitter la saisie demande à l'utilisateur ce qu'il veut en faire (enregistrer en brouillon, abandonner...)
  get mustConfirmDeclarationExit(): boolean {
    return this.isEntryEditable && !!(this.currentEntry?.isModified || this.currentEntry?.isStarted);
  }

  openEntry = (entry: CurrentEntry): void => {
    this.currentEntry = entry;
  };

  markEntryModified = (): void => {
    if (this.currentEntry && !this.currentEntry.isModified)
      this.currentEntry = { ...this.currentEntry, isModified: true };
  };

  closeEntry = (): void => {
    this.currentEntry = undefined;
  };

  setFieldFocused = (path: string | undefined): void => {
    this.fieldFocused = path;
  };

  setCurrentBaseScope = (path: string | undefined): void => {
    this.currentBaseScope = path;
  };

  setIsDeepLink = (isDeepLink: boolean): void => {
    this.isDeepLink = isDeepLink;
  };

  setSendingAttachments = (isSendingAttachements: boolean): void => {
    this.sendingAttachments = isSendingAttachements;
  };

  setSynchronizationStatus = (status: DeclarationSynchronizationStatus): void => {
    this.synchronizationStatus = status;
  };

  setSynchronizationError = (error: string | undefined): void => {
    this.synchronizationError = error;
  };

  setAttachmentsToSend = (newTotalAttachmentToSend: number): void => {
    this.attachmentsToSend = newTotalAttachmentToSend;
  };

  setBackupDclContext = (backupDclContext: LocalBackupDeclaration[]): void => {
    this.backupDclContext = backupDclContext;
  };

  setSendAPI = (sendAPI: boolean): void => {
    this.sendAPI = sendAPI;
  };

  setDeclarationStoreId = (declarationId: string): void => {
    this.declarationId = declarationId;
  };

  setScrollTarget = (target: React.MutableRefObject<HTMLElement | undefined> | undefined) => {
    this.scrollTarget = target;
  };

  setVerifRequiredField = (flag: boolean) => {
    this.verifRequiredField = flag;
  };

  setRequiredField = (field: FieldDesc | undefined) => {
    this.requiredField = field;
  };

  setIsMaxAttachment = (data: boolean) => {
    this.isMaxAttachment = data;
  };

  setIsDeletedDeclarationContext = (status: boolean): void => {
    this.isDeletedDeclarationContext = status;
  };

  setContextRetrievalErrorMessage = (message: string | undefined): void => {
    this.contextRetrievalErrorMessage = message;
  };

  setCurrentStepWorkFlow = (data: StepDesc) => {
    this.currentStepWorkFlow = data;
  };

  setSendingDeclaration = (sendingDeclaration: boolean) => (this.sendingDeclaration = sendingDeclaration);

  setUrlToUseForImagesBank = (url: string | undefined) => {
    this.urlToUseForImagesBank = url;
  };

  /**
   * UO associée à une déclaration (le contexte renvoyé par l'API ne la contient pas) : recherchée dans les listes
   * des brouillons et de l'historique, puis dans la liste locale. undefined si elle n'est pas connue.
   */
  getDeclarationOuCode = async (declarationId: string): Promise<string | undefined> => {
    const inLists = [
      ...(this.DraftsStore.items ?? []),
      ...(this.HistoryStore.items ?? []),
      ...(this.LeftMenuDraftsStore.items ?? []),
      ...(this.LeftMenuHistoryStore.items ?? []),
    ].find((d) => d.id === declarationId && !!d.organizationalUnitCode);
    if (inLists) return inLists.organizationalUnitCode;

    try {
      const localItem = await declarationsDb.declarationListItems.get(declarationId);
      return localItem?.organizationalUnitCode || undefined;
    } catch {
      return undefined;
    }
  };

  resetStore = (): void => {
    this.sendingDeclaration = false;
    this.synchronizationStatus = DeclarationSynchronizationStatus.NotStarted;
    this.synchronizationError = undefined;
    // currentEntry n'est pas réinitialisée ici : la page de la déclaration l'ouvre et la ferme, et peut rester affichée
    // après une sauvegarde (retour à l'étape précédente d'un workflow)
    this.openDeclarationDialog = false;
    this.isDrawerOpened = false;
    this.numberDisplayed = 0;
    this.DraftsStore.reset();
    this.HistoryStore.reset();
    this.LeftMenuDraftsStore.reset();
    this.LeftMenuHistoryStore.reset();
    this.backupDclContext = [];
    this.internalDataDico = new Map<string, FieldData>();
    this.currentBaseScope = undefined;
    this.fieldFocused = undefined;
    this.setUrlToUseForImagesBank(undefined);
  };

  setOpenDrawer = () => {
    this.isDrawerOpened = true;
  };

  setCloseDrawer = () => {
    this.isDrawerOpened = false;
  };

  setOpenConsentPopup = () => {
    this.openConsentPopup = true;
  };

  setCloseConsentPopup = () => {
    this.openConsentPopup = false;
  };

  openDialog = () => {
    this.openDeclarationDialog = true;
  };

  closeDialog = () => {
    this.openDeclarationDialog = false;
  };

  setShouldAbortRequests = (shouldAbortRequests: boolean) => (this.shouldAbortRequests = shouldAbortRequests);

  // Permet d'obetnir la liste des médias à partir d'editedData
  findMediasType(fieldData: FieldData): AttachmentItemData[] {
    const { value } = fieldData;

    // Est-ce que le fieldData est un attachmentItemData?
    if (isAnAttachmentItemsData(value)) return value as AttachmentItemData[];

    // Est-ce que le fieldData provient du TodoList?
    if (isATodoListData(value) && value.taskEditedData.length > 0) {
      return value.taskEditedData
        .filter((x: TaskEditedDataType | undefined) => x !== undefined)
        .reduce(
          (acc: AttachmentItemData[], { attachment }: TaskEditedDataType) => (attachment ? [...acc, attachment] : acc),
          [],
        );
    }
    // Est-ce que c'est un container??
    if (isFieldDataArray(fieldData, value))
      return value.reduce((acc: AttachmentItemData[], current: FieldData) => {
        const result = this.findMediasType(current);
        return result.length > 0 ? [...acc, ...result] : acc;
      }, []);

    // Sinon on retourne []
    return [];
  }

  findFieldInGroup = (obj: FieldDesc, fieldId: string): FieldDesc => {
    if (obj.id === fieldId) {
      return obj;
    }
    for (const i in obj.items) {
      const x = this.findFieldInGroup(obj.items[i], fieldId);
      if (x.id === fieldId) {
        return obj.items[i];
      }
    }
    return obj;
  };

  /**
   * Permet de récupérer une adresse depuis des coordonnées GPS.
   * @param {Position} coordinates
   */
  startReverseGeocodingAsync = async (coordinates: Position): Promise<AddressData | undefined> => {
    try {
      this.logger.resetInitDateTimeApp();
      const { latitude, longitude } = coordinates;

      if (!latitude || !longitude) return undefined;

      // Construction de la requête reverseGeocoding
      const reverseGeocodingURL = buildApiUrl(OMS_GET_REVERSE_GEOCODING_URI, { latitude, longitude });

      const reverseGeocodingResponse = await this.clientHTTP.get<OMSReverseGeocodingResponse>(reverseGeocodingURL);

      if (reverseGeocodingResponse.status !== 200 || !reverseGeocodingResponse.data.address) return undefined;

      const { address } = reverseGeocodingResponse.data;

      return {
        streetNumber: address.house_number ?? '',
        street: address.road ?? '',
        zipCode: address.postcode?.toString() ?? '',
        city: address.suburb ?? address.town ?? address.municipality ?? '',
        complement: '',
        country: address.country ?? '',
        coordinates,
      } as AddressData;
    } catch (error) {
      errorHandler(DeclarationStore.TAG, error, 'startReverseGeocodinAsync');
    }
  };

  getIndexDocumentDTO = async (files: QCAttachment[]): Promise<boolean> => {
    const documentDTO: DocumentIndexDTO[] = [];
    type HashMap = {
      [correlation: string]: QCAttachment;
    };
    const hashMap: HashMap = {};
    //Création des documentDTO pour indexation
    files.map((item) => {
      const documentToAdd = {
        ApplicationName: APP_NAME,
        DocName: item.docName,
        DocContentType: item.docContentType,
        DocTitle: item.docTitle,
        DocCreateDate: item.docCreateDate,
        PurgeDate: item.purgeDate,
        //metadata: item.metadata,
        CorrelationId: item.id,
        Id: item.id,
      } as DocumentIndexDTO;
      hashMap[item.id] = item;
      documentDTO.push(documentToAdd);
    });
    //Création de la request
    const request = {
      NewsDocumentsIndex: documentDTO,
    } as IndexDocumentRequest;
    try {
      if (!this.shouldAbortRequests) {
        this.logger.info(DeclarationStore.TAG, `ATTACHMENT_INDEX_REQUEST - count: ${files.length}`);
        const response = await this.clientHTTP.post<GetIndexDocumentResponse>(
          this.RootStore.CommonStore.chooseBaseUrl(API_GET_DOCUMENT_INDEX),
          request,
          {
            withCredentials: true,
          },
        );
        if (200 <= response.status && response.status <= 299) {
          const { storagesInfos } = response.data;
          const toUpdate: QCAttachment[] = [];
          storagesInfos.forEach((storageInfo) => {
            if (hashMap[storageInfo.correlationId]) {
              //Mise à jour des distantUri à partir du correlationId
              hashMap[storageInfo.correlationId].distantUri = storageInfo.uri;
              hashMap[storageInfo.correlationId].transferState = DocumentTransferState.ToBeTransfered;
              this.logger.log(
                DeclarationStore.TAG,
                `INDEX_DOC_OK - CorrelationId: ${storageInfo.correlationId} - distantUrl: ${storageInfo.uri}`,
              );
              toUpdate.push(hashMap[storageInfo.correlationId]);
            }
          });

          if (toUpdate.length !== files.length) {
            const matchedIds = new Set(toUpdate.map(({ id }) => id));
            this.logger.warn(
              DeclarationStore.TAG,
              `ATTACHMENT_INDEX_INCOMPLETE - requested: ${files.length} - matched: ${toUpdate.length} - missing: ${
                files.filter(({ id }) => !matchedIds.has(id)).length
              }`,
            );
          }

          if (toUpdate.length > 0) {
            //Sauvegarde en BDD des modifications
            const persisted = await attachmentDb.updateAttachments(toUpdate);
            if (!persisted) {
              this.logger.warn(DeclarationStore.TAG, `ATTACHMENT_INDEX_PERSIST_FAILED - count: ${toUpdate.length}`);
            }
            //Mise à jour des distantUris des attachment dans les déclarations
            await this.updateDeclarationWithStorageUri(toUpdate);
          }
          return true;
        } else {
          throw new Error(`INDEX_DOC_ERR - Statut code ${response.status}`);
        }
      }
    } catch (error) {
      errorHandler(DeclarationStore.TAG, error, 'getIndexDocumentDTO', 'warn');
    }
    return false;
  };

  /**
   * Extrait l'historique d'une reponse getrundeclarationviewer. L'API peut repondre en 2xx sans
   * tableau historic (statut applicatif KO, charge utile vide ou non JSON): on trace le contexte et
   * on repart sur une liste vide plutot que de laisser remonter un TypeError.
   *
   * @param response Reponse HTTP renvoyee par l'API.
   * @param userUPN UPN interroge, ajoute a la trace.
   */
  private extractHistoric = (
    response: { status: number; data: GetRunDeclarationViewerResponse | undefined },
    userUPN: string,
  ): DeclarationViewer[] => {
    const { status, data } = response;

    if (status < 200 || status > 299) {
      this.logger.warn(
        DeclarationStore.TAG,
        `DECLARATION_LIST_UNEXPECTED_STATUS - UPN: ${userUPN} - Statut HTTP: ${status}`,
      );
      return [];
    }

    if (data === undefined || data === null || typeof data !== 'object') {
      this.logger.warn(
        DeclarationStore.TAG,
        `DECLARATION_LIST_MALFORMED_PAYLOAD - UPN: ${userUPN} - Statut HTTP: ${status} - Type recu: ${typeof data}`,
      );
      return [];
    }

    if (!Array.isArray(data.historic)) {
      this.logger.warn(
        DeclarationStore.TAG,
        `DECLARATION_LIST_MISSING_HISTORIC - UPN: ${userUPN} - Statut HTTP: ${status} - Statut applicatif: ${
          data.status ?? 'absent'
        } - Code erreur: ${data.errorCode ?? 'absent'} - Message: ${data.message ?? 'absent'}`,
      );
      return [];
    }

    return data.historic;
  };

  /**
   * Trace une erreur survenue pendant la construction de la liste des declarations.
   *
   * @param error Erreur interceptee.
   * @param userUPN UPN interroge, ajoute a la trace.
   */
  private handleGetDeclarationListError = (error: unknown, userUPN: string): void => {
    if (error instanceof AxiosError) {
      const errorAxios = error as AxiosError<GetRunDeclarationViewerResponse>;
      errorAxios.message = `Une erreur est survenue lors de la récupération des formulaires pour cet userUPN ${userUPN}: 
       ${errorAxios?.response?.data?.message ?? errorAxios?.response?.statusText ?? errorAxios.message}`;
      errorHandler(DeclarationStore.TAG, errorAxios, 'getDeclarationList', 'trace');
    } else if (error instanceof Error) {
      error.message = `Une erreur est survenue lors de la récupération des formulaires pour cet userUPN ${userUPN} : ${error.message}`;
      errorHandler(DeclarationStore.TAG, error, 'getDeclarationList', 'error');
    }
  };

  getDeclarationList = async (userUPN: string): Promise<void> => {
    if (this.shouldAbortRequests) return;

    // Historique: isole dans son propre try afin qu'une reponse en erreur ou mal formee n'empeche
    // pas le chargement des brouillons locaux, disponibles hors ligne.
    try {
      const getDeclListUri = this.RootStore.CommonStore.chooseBaseUrl(
        buildApiUrl(API_GET_DECLARATIONS, { forThisUPN: userUPN }),
      );
      const response = await this.clientHTTP.get<GetRunDeclarationViewerResponse>(getDeclListUri, {
        withCredentials: true,
      });

      const declarations = this.extractHistoric(response, userUPN).map((d) => mapDeclarationViewer(d, false));
      // On ordonne par date décroissante
      const uniqueDeclarations = declarations
        .slice()
        .filter((d: DeclarationViewer) => d.folderPath !== 'unclassified')
        .sort((a: DeclarationViewer, b: DeclarationViewer) => {
          return b.editedAt && a.editedAt ? new Date(b.editedAt).getTime() - new Date(a.editedAt).getTime() : -1;
        });
      const sortedByDateDeclarations = uniqueDeclarations.filter(
        (value, index, self) => index === self.findIndex((t) => t.id === value.id),
      );
      this.HistoryStore.setItems(sortedByDateDeclarations);
      this.LeftMenuHistoryStore.setItems(sortedByDateDeclarations);
    } catch (error) {
      this.handleGetDeclarationListError(error, userUPN);
    }

    // Brouillons: uniquement locaux, l'API n'est plus interrogee pour cette partie.
    try {
      const localDrafts = await declarationsDb.getLocalDrafts(userUPN, this.logger);
      const drafts = localDrafts.map((d) => mapDeclarationViewer(d, true));

      const uniqueDrafts = drafts.filter((value, index, self) => index === self.findIndex((t) => t.id === value.id));
      // On ordonne par date décroissante
      const sortedByDateDrafts = uniqueDrafts.slice().sort((a, b) => {
        return b.editedAt && a.editedAt ? new Date(b.editedAt).getTime() - new Date(a.editedAt).getTime() : -1;
      });
      this.DraftsStore.setItems(sortedByDateDrafts);
      this.LeftMenuDraftsStore.setItems(sortedByDateDrafts);
    } catch (error) {
      this.handleGetDeclarationListError(error, userUPN);
    }
  };

  clearDraftsAndHistoricItems = () => {
    this.DraftsStore.reset();
    this.LeftMenuDraftsStore.reset();
    this.HistoryStore.reset();
    this.LeftMenuHistoryStore.reset();
  };

  handleWrittingHistory = (declaration: FieldDesc[]) => {
    flatten(declaration, (i) => i.items)
      .filter((i) => i.lruCount)
      .forEach(async (x: FieldDesc) => {
        if (x.lruCount) {
          const lru = await fieldSearchHistoryDb.getFieldHistoryById(x.id);

          if (x.fieldType === FieldType.Notification) {
            const { value, fullPathId } = x as NotificationDesc;
            if (value) {
              try {
                const { selectedTargets } = value as QCNotification;
                if (selectedTargets.length > 0) {
                  const description = [
                    ...(lru?.description ?? []),
                    ...selectedTargets.map((nt: NotificationTarget) => nt.target),
                  ];
                  const {
                    LoginStore: {
                      signInInfos: { userUPN },
                    },
                  } = this.RootStore;
                  const objToModify: IFieldSearchHistory = {
                    description,
                    id: getLRUFullId(declaration, fullPathId.split('.')),
                    userUPN,
                  };
                  await fieldSearchHistoryDb.modifyFieldHistory(objToModify);
                }
              } catch (error) {
                // eslint-disable-next-line no-empty
              }
            }
          } else if (x.fieldType === FieldType.Text) {
            const { value, fullPathId } = x as EditDesc;

            if (value && typeof value === 'string' && value !== '') {
              const description = [...(lru?.description ?? []), value];
              const {
                LoginStore: {
                  signInInfos: { userUPN },
                },
              } = this.RootStore;
              const objToModify: IFieldSearchHistory = {
                description,
                id: getLRUFullId(declaration, fullPathId.split('.')),
                userUPN,
              };
              await fieldSearchHistoryDb.modifyFieldHistory(objToModify);
            }
          }
        }
      });
  };

  /**
   * Getion du libellé calculé
   * @param declaration
   * @param stringExtract
   * @param signInInfos
   * @param context
   * @returns
   */
  static async calculateLabel(
    declaration: FieldDesc[],
    stringExtract: string,
    signInInfos: SignInResponse,
    context: DeclarationContext,
  ) {
    /**
     * Pattern RegEx pour la découverte des tokens dans le libellé calculé
     */
    const sPatternSystemRefForStringExtract = new RegExp('SystemRef\\s*[(]\\s*(\\w+)\\s*[)]', 'g');
    const sPatternEquipementForStringExtract = new RegExp('\\[\\s*([^\\]:\\s]*)\\s*[:]*\\s*(\\s*\\S*\\s*)\\]', 'g');
    const sPatternFieldForStringExtract = new RegExp(
      '\\{\\s*(?<fieldId>[^\\}:\\s]*)\\s*[:]*\\s*(?<format>\\s*\\d*[.,]*\\d*\\s*)\\}',
      'mg',
    );

    let labelValue = stringExtract;
    if (stringExtract !== undefined) {
      const fieldTokens = [...stringExtract.matchAll(sPatternFieldForStringExtract)];
      const userInfoTokens = stringExtract.match(sPatternSystemRefForStringExtract);
      const equipementTokens = stringExtract.match(sPatternEquipementForStringExtract);

      if (equipementTokens) {
        labelValue = await buildCalculatedLabelWithEquipmentTokens(
          labelValue,
          equipementTokens,
          context.entityInstanceId,
        );
      }

      if (fieldTokens) {
        labelValue = buildCalculatedLabelWithFieldTokens(labelValue, fieldTokens, declaration);
      }

      if (userInfoTokens) {
        labelValue = buildCalculatedLabelWithSystemRefTokens(labelValue, userInfoTokens, signInInfos);
      }

      return labelValue;
    }
    return context.name;
  }

  saveDeclarationAsync = async (
    context: DeclarationContext,
    declaration: FieldDesc[],
    userUpn: string,
    organizationalUnitName: string,
    t: TFunction,
    isDraft: boolean,
    itemData: ItemData | undefined,
    activityId: string | undefined,
    inboxId: string | undefined,
    isOnline: boolean,
    declarationId: string,
    signInInfos: SignInResponse,
    sendAPI?: boolean | undefined,
  ): Promise<boolean> => {
    try {
      if (declaration) {
        this.handleWrittingHistory(declaration);
      }
      if (context) {
        // RAZ de l'horodatage
        this.logger.resetInitDateTimeApp();
        this.logger.trace(DeclarationStore.TAG, 'Début de la sauvegarde de la déclaration');

        const { language } = window.navigator;
        const languageCode = language.substring(0, 2);

        // Est-ce que la déclaration possède des PJ
        const attachmentsFile: AttachmentItemData[] = (
          await attachmentDb.getAttachmentItemDataByDeclarationId(declarationId)
        ).map(mapFromQCAttachmentToAttachmentItemData);
        this.logger.debug(
          DeclarationStore.TAG,
          `ATTACHMENT_VALIDATION_SNAPSHOT - declarationId: ${declarationId} - sendAPI: ${sendAPI === true} - count: ${
            attachmentsFile.length
          } - ids: ${attachmentsFile.map(({ id }) => id).join(',')}`,
        );
        // Modification de la declarationId pour les attachments
        const indexationFlag = sendAPI === true && isDraft === false;
        if (attachmentsFile.length > 0)
          if (context.formType === FormType.Workflow) {
            // Attention dans les cas des Wkf, il faut s'assurer d'envoyer seulement celles de l'étape courante...
            const currentStepId = itemData?.Workflow?.currentStep;
            if (!currentStepId) throw new Error(`Pas de currentStepId pour ce workflow: ${context.name}`);

            const currentStepField = declaration.filter(
              ({ id }: FieldDesc) => id.localeCompare(currentStepId, undefined, { sensitivity: 'base' }) === 0,
            );

            if (currentStepField.length === 0)
              throw new Error(`Pas de currentStepField pour ce workflow: ${context.name}`);

            const attachIdsToSend =
              flatten(currentStepField, (i) => i.items)
                .filter(
                  ({ fieldType, value }: FieldDesc) => IsAnAttachmentField(fieldType) && isAnAttachmentItemsData(value),
                )
                .reduce((acc: string[], current: FieldDesc) => {
                  return [...acc, ...(current.value as AttachmentItemData[]).map(({ id }: AttachmentItemData) => id)];
                }, []) ?? [];
            await attachmentDb.updateAttachmentItemDataForWorkFlow(
              attachmentsFile,
              declarationId,
              indexationFlag,
              attachIdsToSend,
            );
          } else {
            await attachmentDb.updateDeclIdForAttachment(attachmentsFile, declarationId, indexationFlag);
          }
        if (indexationFlag) await this.traceAttachmentsNotIndexable(declaration, declarationId);

        // Calcul du champ calculé s'il en existe un
        const stringExtractDisplayed = await DeclarationStore.calculateLabel(
          declaration,
          context.stringExtract,
          signInInfos,
          context,
        );

        const editedData = await this.getEditedData(
          declarationId,
          context,
          userUpn,
          declaration,
          attachmentsFile,
          organizationalUnitName,
          languageCode,
          itemData,
          activityId,
          inboxId,
          isDraft,
          sendAPI,
          t,
          stringExtractDisplayed,
        );

        // Si il y a internet et qu'on n'est pas dans une sauvegarde automatique en local, on envoie la déclaration à l'API
        if (sendAPI && !isDraft) {
          editedData.stringExtract = (await DeclarationStore.calculateLabel(
            declaration,
            context.stringExtract,
            signInInfos,
            context,
          )) as string;
          // On enregistre d'abord en base locale

          // On se servira du customerName comme référence (avant c'était la base uri mais cet observable n'est pas toujours set)
          const { urlToUse } = this.RootStore.LoginStore;
          const { getRGPDConsentsToSend } = this.RootStore.ConsentStore;
          const synchronizeDeclarationQuery = {
            userUPN: userUpn,
            imei: getSessionId(),
            userAgentDevice: window.navigator.userAgent,
            typeDevice: getDeviceType(),
            applicationName: APP_NAME,
            appVersion: APP_VERSION,
            qcLibVersion: FormLibVersion,
            consentsRGPD: await getRGPDConsentsToSend(),
            languageCode: languageCode,
            editedData: [convertFromFormEditedData(editedData)],
            customerId: urlToUse,
            transferState: DocumentTransferState.ToBeTransfered,
          } as SynchronizeDeclaration;
          const declarationSaved = await declarationsDb.addDeclarationToUpload(synchronizeDeclarationQuery);

          if (declarationSaved) {
            // Suppression de la déclaration dans la base locale
            await this.removeBackUpDcl(declarationId);
            await declarationsDb.removeDraftDeclarationById(declarationId);
            this.DraftsStore.removeItem(declarationId);
            this.LeftMenuDraftsStore.removeItem(declarationId);

            if (
              context.formType === FormType.Workflow &&
              itemData?.Workflow?.currentWorkflowState === WorkflowStatus.ToForward
            ) {
              // On fake l'itemData pour eviter de sauter les etapes...
              const contextWithEditedData: DeclarationContext = {
                ...context,
                id: declarationId,
                itemData: itemData,
                editedData: editedData.editedData,
                stringExtract: context.stringExtract,
                internalData: editedData.internalData,
                // Activité du workflow : l'étape suivante, reprise après fermeture de l'onglet, poursuit le même workflow
                activityId: editedData.activityId,
              };
              await declarationsDb.insertBackUpDeclarationContext(
                contextWithEditedData,
                userUpn,
                stringExtractDisplayed,
                this.logger,
                inboxId,
              );

              return true;
            }

            if (!(await declarationsDb.deleteListItemsDeclaration(declarationId))) {
              throw new Error(
                `La suppression de la liste locale des elements de la déclaration a échoué pour la déclaration ${declarationId}`,
              );
            }
            this.RootStore.QCScriptStore.resetStore();
            this.RootStore.ConsentStore.resetStore();
            this.resetStore();
            this.setSendingDeclaration(true);
            this.setIsMaxAttachment(false);
            this.setSendAPI(true);
            this.internalDataDico = new Map<string, FieldData>();
            return true;
          } else {
            toast.error(t('saveDeclarationAsync.error').toString());
            return false;
          }
        }
        // sinon sauvegarde en BDD de la déclaration
        else {
          if (isDraft) {
            await this.saveInLocalDraftDeclarationsTable(editedData, context, itemData, declarationId);
            toast.success(t('pause_declaration_success', { name: editedData.stringExtractDisplayed }).toString());

            this.RootStore.QCScriptStore.resetStore();
            this.RootStore.ConsentStore.resetStore();
            this.resetStore();
            this.internalDataDico = new Map<string, FieldData>();

            return true;
          } else {
            try {
              let newItemData = context.itemData;
              if (
                context.formType === FormType.Workflow &&
                itemData?.Workflow?.currentWorkflowState === WorkflowStatus.ToForward
              ) {
                // On fake l'itemData pour eviter de sauter les etapes...
                newItemData = {
                  ...itemData,
                  Workflow: { ...itemData.Workflow, currentWorkflowState: WorkflowStatus.None },
                };
              }
              const newContext = { ...context, itemData: newItemData };
              const contextWithEditedData: DeclarationContext = {
                ...newContext,
                id: declarationId,
                itemData: newItemData,
                editedData: editedData.editedData,
                stringExtract: context.stringExtract,
                internalData: editedData.internalData,
                // Activité du workflow : la reprise poursuit le même workflow au lieu d'en créer un nouveau
                activityId: editedData.activityId,
              };
              await declarationsDb.insertBackUpDeclarationContext(
                contextWithEditedData,
                userUpn,
                stringExtractDisplayed,
                this.logger,
                inboxId,
              );
            } catch (error) {
              if (error instanceof Error) {
                error.message = `Sauvegarde dans l'indexedDB a échoué: ${error.message}`;
                errorHandler(DeclarationStore.TAG, error, 'saveDeclarationAsync', 'error');
              }
            }
          }
        }
      }
    } catch (error) {
      errorHandler(DeclarationStore.TAG, error, 'saveDeclarationAsync', 'error');

      if (error instanceof AxiosError) {
        const errorAxios = error as AxiosError<any>;
        const errorMsg: unknown = errorAxios?.response?.data?.message;
        // Sans réponse exploitable (réseau, timeout...), on évite d'afficher "undefined"
        toast.error(
          (errorMsg
            ? t('qcapp_warning_service_send_action_error', {
                msg: `${errorMsg}`,
              })
            : t('qcapp_warning_api_unavailable')
          ).toString(),
        );
      } else if (error instanceof Error) {
        const errorMsg = error.message;
        toast.error(
          t('qcapp_warning_service_send_action_error', {
            msg: errorMsg,
          }).toString(),
        );
      }
    }
    this.setSendingDeclaration(false);
    return false;
  };

  /**
   * Sauvegarde la déclaration dans la table DraftDeclarations
   * @param editedData
   * @param context
   * @param declarationId
   */
  saveInLocalDraftDeclarationsTable = async (
    editedData: FormEditedData,
    context: DeclarationContext,
    itemData?: ItemData,
    declarationId?: string,
  ): Promise<void | undefined> => {
    try {
      // Sauvegarde dans la table draftDeclarations
      editedData.isDraft = true;

      context.isDraft = true;
      context.editedData = editedData.editedData;
      context.id = declarationId;
      context.itemData = itemData;

      // Important : on conserve l'activityId d'origine lors de la mise en pause
      // Sinon, un nouvel activityId est généré à la reprise, ce qui casse la continuité du workflow
      context.activityId = editedData.activityId;

      // Internal Data
      context.internalData = editedData.internalData;

      // UO de saisie : le brouillon sera repris dans cette UO
      if (editedData.ouCode) context.organizationalUnitCode = editedData.ouCode;

      const localDraftDeclaration: LocalBackupDeclaration = {
        ...context,
        userUPN: editedData.userUpn,
        stringExtractDisplayed: editedData.stringExtractDisplayed,
      };

      if (declarationId) {
        const draftedDeclaration = await declarationsDb.draftDeclarations.get(declarationId);

        if (draftedDeclaration) {
          await declarationsDb.draftDeclarations.put(localDraftDeclaration, declarationId);
          const declarationListItems = await declarationsDb.declarationListItems.get(declarationId);
          await declarationsDb.declarationListItems.put(
            {
              ...declarationListItems,
              editedAt: new Date(),
              stringExtract: editedData.stringExtract,
              organizationalUnitCode: context.organizationalUnitCode,
            } as DeclarationViewer,
            declarationId,
          );
        } else {
          await declarationsDb.draftDeclarations.add({ ...localDraftDeclaration, editedAt: new Date() }, declarationId);
        }
        const { name, formId, folderPath, formType } = context;
        const { stringExtract } = editedData;

        // Vérifions s'il existe aussi dans la table listItems
        const existinglistItemsDeclaration = await declarationsDb.declarationListItems.get(declarationId);

        if (existinglistItemsDeclaration) {
          await declarationsDb.declarationListItems.put(
            {
              ...existinglistItemsDeclaration,
              isDraft: true,
              editedAt: new Date(),
            } as DeclarationViewer,
            declarationId,
          );
        } else {
          // On sauvegarde l'élément de la liste
          await declarationsDb.declarationListItems.add({
            id: declarationId,
            name,
            editedAt: new Date(),
            formId,
            formType,
            folderPath,
            stringExtract,
            organizationalUnitCode: context.organizationalUnitCode,
            isDraft: true,
            isLocal: true,
          } as DeclarationViewer);
        }

        await this.removeBackUpDcl(declarationId);
      }
    } catch (error) {
      errorHandler(DeclarationStore.TAG, error, 'saveInLocalDraftDeclarationsTable');
    }
  };

  getFileByRemoteAPI = async (distantUri: string, filename: string, type: string): Promise<File | undefined> => {
    try {
      const response = await this.clientHTTP.get<GetFileByRemoteAPIResponse>(distantUri, {
        responseType: 'blob',
        withCredentials: true,
      });

      if (response.status <= 200 && response.status < 299) {
        const file = new File([response.data], filename, { type });

        return file;
      }
    } catch (error) {
      const errorAxios = error as AxiosError<unknown>;
      if (errorAxios.response?.status === 404) {
        return;
      } else {
        errorHandler(DeclarationStore.TAG, error, 'getFileByRemoteAPI');
      }
    }
  };

  getDeclarationContext = async (
    url: string,
    declarationId: string | undefined,
    entityInstanceId?: string,
    isInbox?: boolean,
  ): Promise<DeclarationContext | undefined> => {
    try {
      this.setContextRetrievalErrorMessage(undefined);
      if (!this.shouldAbortRequests) {
        const response = await this.clientHTTP.get<GetDeclarationContextResponse>(url, { withCredentials: true });

        if (!response.data.declarationContext) return;
        const mapped = mapDeclarationContext(response.data.declarationContext, entityInstanceId, isInbox);
        // Si la donnée n'est pas côté serveur on check coté bdd locale
        if (!mapped.editedData && declarationId) {
          const data = await declarationsDb.declarations.get(declarationId);
          if (data) {
            mapped.id = data.id;
            mapped.editedData = data.editedData;
            mapped.activityId = data.activityId;
            mapped.entityInstanceId = data.entityInstanceId;
          }
        }

        // On indique qu'on ouvre une déclaration nouvelle ou existante.
        return mapped;
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorAxios = error as AxiosError<GetDeclarationContextResponse>;
        // Message affiché à l'utilisateur (réponse de l'API, page d'erreur HTML d'une passerelle, API injoignable)
        this.setContextRetrievalErrorMessage(getDisplayableErrorMessage(errorAxios));
        errorAxios.message = `Une erreur est survenue lors de la récupération du formulaire : ${errorAxios?.response?.data?.status}  ${errorAxios?.response?.data?.message}`;
        errorHandler(DeclarationStore.TAG, errorAxios, 'getDeclarationContext');
      } else if (error instanceof Error) {
        error.message = `Une erreur est survenue lors de la récupération du formulaire : ${error.message}`;
        errorHandler(DeclarationStore.TAG, error, 'getDeclarationContext');
      }
      this.setIsDeletedDeclarationContext(true);
    }
  };

  getFormContext = async (url: string, entityInstanceId?: string): Promise<DeclarationContext | undefined> => {
    try {
      this.setContextRetrievalErrorMessage(undefined);
      if (!this.shouldAbortRequests) {
        const response = await this.clientHTTP.get<GetDeclarationContextResponse>(url, { withCredentials: true });

        if (!response.data.declarationContext) return;

        // On indique qu'on ouvre une déclaration nouvelle ou existante.
        return mapDeclarationContext(response.data.declarationContext, entityInstanceId);
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const axiosError = error as AxiosError<GetDeclarationContextResponse>;
        const responseData = axiosError?.response?.data;

        // Message affiché à l'utilisateur (réponse de l'API, page d'erreur HTML d'une passerelle, API injoignable)
        this.setContextRetrievalErrorMessage(getDisplayableErrorMessage(axiosError));

        // Le corps de la reponse ne suit pas toujours le contrat BaseResponse: erreur emise par la
        // passerelle, page d'authentification, ProblemDetails ASP.NET... Dans ce cas on conserve le
        // statut HTTP et le message d'origine plutot que d'ecrire deux "undefined" dans la trace.
        axiosError.message =
          responseData?.status !== undefined || responseData?.message !== undefined
            ? `Une erreur est survenue lors de la récupération du formulaire : ${responseData?.status}  ${responseData?.message}`
            : `Une erreur est survenue lors de la récupération du formulaire - Status: ${
                axiosError?.response?.status ?? 'aucune reponse'
              } - ${axiosError.message}`;

        errorHandler(DeclarationStore.TAG, error, 'getFormContext', 'error');
      } else if (error instanceof Error) {
        error.message = `Une erreur est survenue lors de la récupération du formulaire : ${error.message}`;
        errorHandler(DeclarationStore.TAG, error, 'getFormContext', 'error');
      }
    }
  };

  /**
   * Contexte d'une saisie interrompue, depuis sa sauvegarde automatique. Undefined si la saisie a été terminée entre-temps
   * ou si la sauvegarde ne correspond pas au formulaire, à l'utilisateur ou à l'élément de la boîte de réception ouverts.
   */
  getBackupContextAsync = async (
    backupDeclarationId: string,
    formId: string | undefined,
    userUPN: string,
    inboxId: string | undefined,
  ): Promise<DeclarationContext | undefined> => {
    const backup = await declarationsDb.getBackUpDeclarationContext(backupDeclarationId);
    if (!backup || backup.formId !== formId || backup.userUPN !== userUPN || backup.inboxId !== inboxId) return;

    const { userUPN: backupUserUPN, inboxId: backupInboxId, ...context } = backup;
    return context;
  };

  getFormContextByFormNameAsync = async (params: URLSearchParams): Promise<DeclarationContext | undefined> => {
    try {
      if (this.shouldAbortRequests) return;

      const formName: string = params.get('formname')!;
      const exchangeId = params.get('exchangeid');
      const exchangeData = params.get('exchangedata');

      const {
        CommonStore: { chooseBaseUrl },
      } = this.RootStore;
      const deepLinkUrl: string = exchangeId
        ? chooseBaseUrl(buildApiUrl(API_GET_FORM_CONTEXT_DEEP_LINK, { formName, exchangeId }))
        : chooseBaseUrl(buildApiUrl(API_GET_FORM_CONTEXT_DEEP_LINK_WITHOUT_EXCHANGE, { formName }));

      const getFormContextDeepLinkResponse = await this.clientHTTP.get<GetDeclarationContextResponse>(deepLinkUrl, {
        withCredentials: true,
      });

      if (!getFormContextDeepLinkResponse.data.declarationContext)
        throw new Error(`no declarationContext data provided`);

      if (exchangeData && !exchangeId) {
        getFormContextDeepLinkResponse.data.declarationContext.formContextDto.injectedData = exchangeData;
      }

      return mapDeclarationContext(getFormContextDeepLinkResponse.data.declarationContext, undefined);
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw error;
      }

      throw new Error(`Unknown Error`);
    }
  };

  removeLocalDeclaration = async (declarationId: string, deleteInDeclarationTable?: boolean): Promise<void> => {
    try {
      if (deleteInDeclarationTable && declarationId) {
        const deletedRecordDeclaration = await declarationsDb.removeDeclaration(this.logger, declarationId);

        if (deletedRecordDeclaration)
          this.logger.log(
            DeclarationStore.TAG,
            `${deletedRecordDeclaration} déclarations de la table locale déclarations ont été supprimées`,
          );

        // Suppression en table des brouillons et de la sauvegarde automatique
        await declarationsDb.removeDraftDeclarationById(declarationId);
        await this.removeBackUpDcl(declarationId);
      }

      if (this.DraftsStore.items && this.DraftsStore.items.length > 0) {
        const newDraftsLists = this.DraftsStore.items.filter(
          (declarationViewer: DeclarationViewer): boolean => declarationViewer.id !== declarationId,
        );
        this.DraftsStore.setItems(newDraftsLists);
      }
    } catch (error) {
      errorHandler(DeclarationStore.TAG, error, 'removeLocalDeclaration');
    }
  };

  private updateDeclarationWithStorageUri = async (attachments: QCAttachment[]) => {
    const dicoDeclaration: KeyValuePair<string, FormEditedData>[] = [];
    const dicoAttachment: KeyValuePair<string, QCAttachment[]>[] = [];

    for (const attach of attachments) {
      //Récupération de toutes les déclarations à mettre à jour
      const decl = await declarationsDb.getDeclaration(attach.declarationId);
      if (decl != undefined) {
        dicoDeclaration.push({ key: attach.declarationId, value: decl });
      }

      //Ajout dans la hashmap dicoAttachment
      const value = dicoAttachment.filter((item) => item.key === attach.declarationId);
      if (value.length > 0) {
        value[0].value.push(attach);
      } else {
        const attachByDecl = [];
        attachByDecl.push(attach);
        dicoAttachment.push({ key: attach.declarationId, value: attachByDecl });
      }
    }

    //Traitement par déclaration
    const declarationsToUpdate: FormEditedData[] = [];
    dicoDeclaration.map((item) => {
      const attachDataString = item.value.attachmentData ?? '';
      // Dé sérialise les attachmentsItemData comme un dico
      const attachData = new Map<string, AttachmentItemData>(JSON.parse(attachDataString));
      const listAttach = dicoAttachment.find((list) => list.key === item.key)?.value;
      if (listAttach) {
        // Mettre à jour le storage URI
        let toSave = false;
        listAttach.map((attach) => {
          const attachToUpdate = attachData.size > 0 ? attachData.get(attach.id) : undefined;
          if (attachToUpdate != undefined) {
            toSave = true;
            attachToUpdate.distantUri = attach.distantUri;
          }
        });

        if (toSave) {
          const newData = JSON.stringify(Array.from(attachData.entries()));
          item.value.attachmentData = newData;
          //Update editedData
          item.value.editedData = this.updateEditedDataWithStorageUri(item.value.editedData, attachData);
          declarationsToUpdate.push(item.value);
        }
      }
    });
    //Sauvegarde des modifications
    if (declarationsToUpdate.length > 0) {
      await declarationsDb.declarations.bulkPut(declarationsToUpdate);
    }
  };

  private updateEditedDataWithStorageUri = (
    editedData: string,
    attachData: Map<string, AttachmentItemData>,
  ): string => {
    const fd: FieldData[] = JSON.parse(editedData) as FieldData[];
    const declUpdated: FieldData[] = [];
    const modified = this.recursiveUpdateEditedDataWithStorageUri(fd, attachData, declUpdated);
    if (modified) {
      const newEditedData = JSON.stringify(declUpdated);
      return newEditedData;
    }
    return editedData;
  };

  private recursiveUpdateEditedDataWithStorageUri = (
    fieldDataList: FieldData[],
    attachData: Map<string, AttachmentItemData>,
    declUpdated: FieldData[] | undefined,
  ): boolean => {
    let result = false;
    fieldDataList.forEach((fd) => {
      if (
        fd.fieldType === FieldType.Group ||
        fd.fieldType === FieldType.RepeatableGroup ||
        fd.fieldType === FieldType.Include ||
        fd.fieldType === FieldType.Dialog ||
        fd.fieldType === FieldType.Step
      ) {
        const childModified = this.recursiveUpdateEditedDataWithStorageUri(
          fd.value as FieldData[],
          attachData,
          undefined,
        );

        if (childModified) result = true;
      } else if (
        fd.fieldType === FieldType.Attachment ||
        fd.fieldType === FieldType.Draw ||
        fd.fieldType === FieldType.Signature ||
        fd.fieldType === FieldType.Photo
      ) {
        const attList = fd.value as AttachmentItemData[];

        attList?.forEach((attach) => {
          const indexedAttachment = attachData.get(attach.id);

          if (indexedAttachment) {
            attach.distantUri = indexedAttachment.distantUri;
            result = true;
          }
        });
      }

      if (declUpdated) {
        declUpdated.push(fd);
      }
    });

    return result;
  };

  modifyFormEditedData = async (
    formEditedDataDTO: FormEditedDataDTO,
    notIndexed: QCAttachment[],
    attachmentsFile: AttachmentItemData[],
  ): Promise<FormEditedDataDTO> => {
    await Promise.all(
      attachmentsFile.map(async (attach) => {
        const indexedAttach = await attachmentDb.attachments.get(attach.id);
        if (indexedAttach?.distantUri) {
          attach.distantUri = indexedAttach.distantUri;
        }
      }),
    );

    const distantUrisByAttachmentId = new Map<string, string>();
    notIndexed.forEach(({ id, distantUri }) => {
      if (distantUri) distantUrisByAttachmentId.set(id, distantUri);
    });
    attachmentsFile.forEach(({ id, distantUri }) => {
      if (distantUri) distantUrisByAttachmentId.set(id, distantUri);
    });
    const data = JSON.parse(formEditedDataDTO.editedData) as FieldData[];
    data.forEach((fieldData) => {
      this.findMediasType(fieldData).forEach((attachment) => {
        const distantUri = distantUrisByAttachmentId.get(attachment.id);
        if (distantUri !== undefined) {
          attachment.distantUri = distantUri;
          attachment.file = undefined;
        }
      });
    });

    this.logger.log(DeclarationStore.TAG, 'modifyFormEditedData finished');
    formEditedDataDTO.editedData = JSON.stringify(data);
    return formEditedDataDTO;
  };

  /**
   * Trace les pièces jointes du formulaire (TodoList incluses) qui ne seront pas indexées et partiront donc
   * sans distantUri: sans ligne locale, rattachées à une autre déclaration, ou non marquées à envoyer.
   *
   * @param declaration Les champs de la déclaration validée.
   * @param declarationId L'id de la déclaration validée.
   */
  private traceAttachmentsNotIndexable = async (declaration: FieldDesc[], declarationId: string): Promise<void> => {
    try {
      const idsWithoutUri = Array.from(
        new Set(
          flatten(declaration, (i) => i.items)
            .flatMap((field) => this.findMediasType(field as FieldData))
            .filter(({ distantUri }) => !distantUri)
            .map(({ id }) => id),
        ),
      );

      const details = await Promise.all(
        idsWithoutUri.map(async (id) => {
          const row = await attachmentDb.getAttachmentMetadataById(id);
          const indexable =
            row?.declarationId === declarationId &&
            (!!row.distantUri || row.transferState === DocumentTransferState.ToBeTransfered);
          return indexable
            ? undefined
            : `${id} [dcl: ${row?.declarationId ?? 'aucune ligne'} - state: ${row?.transferState}]`;
        }),
      );

      const notIndexable = details.filter((detail) => detail !== undefined);
      if (notIndexable.length > 0)
        this.logger.warn(
          DeclarationStore.TAG,
          `ATTACHMENT_VALIDATION_NOT_INDEXABLE - declarationId: ${declarationId} - ${notIndexable.join(' | ')}`,
        );
    } catch (error: unknown) {
      errorHandler(DeclarationStore.TAG, error, 'traceAttachmentsNotIndexable', 'warn');
    }
  };

  sendDeclarationToAPIAsync = (t: TFunction, tAxios?: TFunction): Promise<boolean> => {
    if (this.synchronizationPromise) return this.synchronizationPromise;

    this.setSynchronizationError(undefined);
    const synchronizationPromise = this.runDeclarationSynchronization(t, tAxios);
    this.synchronizationPromise = synchronizationPromise;
    void synchronizationPromise.then(
      (result) => {
        if (!result) {
          if (this.synchronizationStatus !== DeclarationSynchronizationStatus.NotStarted) {
            this.setSynchronizationStatus(DeclarationSynchronizationStatus.Failed);
          }
          const errorMessage = "La synchronisation n'a pas pu être terminee.";
          this.setSynchronizationError(errorMessage);
          toast.error(t('qcapp_warning_service_send_action_error', { ns: 'axios', msg: errorMessage }));
        }
        if (this.synchronizationPromise === synchronizationPromise) this.synchronizationPromise = undefined;
      },
      () => {
        if (this.synchronizationStatus !== DeclarationSynchronizationStatus.NotStarted) {
          this.setSynchronizationStatus(DeclarationSynchronizationStatus.Failed);
        }
        const errorMessage = "La synchronisation n'a pas pu être terminee.";
        this.setSynchronizationError(errorMessage);
        toast.error(t('qcapp_warning_service_send_action_error', { ns: 'axios', msg: errorMessage }));
        if (this.synchronizationPromise === synchronizationPromise) this.synchronizationPromise = undefined;
      },
    );
    return synchronizationPromise;
  };

  private runDeclarationSynchronization = async (t: TFunction, tAxios?: TFunction): Promise<boolean> => {
    // Check browser support for Web Locks API
    if (!navigator.locks) {
      this.logger.warn(DeclarationStore.TAG, 'Web Locks API not supported, proceeding without cross-tab coordination');
      return this.sendDeclarationToAPI(t, tAxios);
    }

    try {
      this.logger.log(DeclarationStore.TAG, 'Requesting declaration sync lock...');
      return await navigator.locks.request('declaration-sync', async (lock) => {
        this.logger.log(DeclarationStore.TAG, 'Declaration sync lock acquired');
        const result = await this.sendDeclarationToAPI(t, tAxios);
        return result;
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : JSON.stringify(error);
      this.logger.error(DeclarationStore.TAG, `Failed to acquire sync lock: ${errorMessage}`);
      errorHandler(DeclarationStore.TAG, error, 'sendDeclarationToAPIAsync');
      throw error;
    }
  };

  private sendDeclarationToAPI = async (t: TFunction, tAxios?: TFunction): Promise<boolean> => {
    const {
      LoginStore: { urlToUse },
    } = this.RootStore;
    if (typeof urlToUse === 'string') {
      const requests: SynchronizeDeclaration[] = await declarationsDb.getDeclarationsToUpload(urlToUse);

      if (requests.length === 0) {
        this.logger.log(DeclarationStore.TAG, `Aucune requête depuis la table declarationToUpload à envoyer`);
      }
      requests.forEach((request: SynchronizeDeclaration) => {
        const { editedData } = request;
        editedData.forEach((dataEdited: FormEditedDataDTO) => {
          const { stringExtract, userUpn, editedAt } = dataEdited;
          // Début de l'envoi des dcl
          this.logger.info(
            DeclarationStore.TAG,
            `[Client Web] - Evenement "sendDeclarationToAPIAsync": Envoi par l'utilisateur ${userUpn} pour la dcl ${stringExtract} le ${
              editedAt as string
            } Début du transfert le ${format(new Date(), dateFormatWithMillisecond)}.`,
          );
        });
      });

      let allAttachmentAreIndexed = true;

      // Récupérer toutes les pièces jointes non indexées dans la BDD
      const notIndexed = await attachmentDb.getAttachmentToIndex();

      if (notIndexed.length > 0) {
        this.setSynchronizationStatus(DeclarationSynchronizationStatus.IndexingAttachments);
        allAttachmentAreIndexed = await this.getIndexDocumentDTO(notIndexed);

        if (allAttachmentAreIndexed) {
          this.setSynchronizationStatus(DeclarationSynchronizationStatus.SynchronizingDeclaration);
        }
      }

      const okDeclarationSent = await Promise.all(
        requests.map(async (requestSync: SynchronizeDeclaration) => {
          const { customerId: custom, transferState, ...request } = requestSync;

          const { editedData: formsEditedDataDto } = request;

          const newFormsEditedDataDto = formsEditedDataDto.map(async (formEditedDataDto: FormEditedDataDTO) => {
            const attachmentItemDataforFEDDto: AttachmentItemData[] = (
              await attachmentDb.getAttachmentItemDataByDeclarationId(formEditedDataDto.id)
            ).map(mapFromQCAttachmentToAttachmentItemData);

            const newFormEditedDataDto = await this.modifyFormEditedData(
              formEditedDataDto,
              notIndexed,
              attachmentItemDataforFEDDto,
            );

            return newFormEditedDataDto;
          });

          request.editedData = await Promise.all(newFormsEditedDataDto);

          // Consentement
          request.consentsRGPD = await this.RootStore.ConsentStore.transferringConsents();

          if (!this.shouldAbortRequests && allAttachmentAreIndexed) {
            const response = await this.clientHTTP.post<SynchronizeDeclarationResponse>(
              this.RootStore.CommonStore.chooseBaseUrl(API_SYNCHRONIZE_DECLARATION),
              request,
              {
                withCredentials: true,
                headers: {
                  // Overwrite Axios's automatically set Content-Type
                  'Content-Type': 'application/json',
                },
              },
            );
            if (200 <= response.status && response.status <= 299) {
              const infoMsg = `L'envoi de la déclaration a réussi`;

              // Info log sur les dcl
              request.editedData.forEach((dataEdited: FormEditedDataDTO) => {
                const { stringExtract, userUpn } = dataEdited;
                // Début de l'envoi des dcl
                this.logger.info(
                  DeclarationStore.TAG,
                  `Evenement "sendDeclarationToAPIAsync": Envoi par l'utilisateur ${userUpn} pour la dcl ${stringExtract} le ${
                    dataEdited.editedAt as string
                  } Fin du transfert le ${format(new Date(), dateFormatWithMillisecond)}.`,
                );
              });

              // Informer l'utilisateur que les déclarations sont transférés après synchro
              const count = request.editedData.length;
              toast.success(t('qcapp_sync_message_declarations', { count }));

              // Remove only the successfully processed declarations
              const processedIds = request.editedData.map((data) => data.id);
              await declarationsDb.removeProcessedDeclarations(request.userUPN, processedIds);

              // Gestion des notifications des logs
              return true;
            } else {
              const error = new Error(
                `Une erreur est survenue lors de l'envoi de la déclaration: Statut: ${response.data.status}, Message: ${response.data.message}`,
              );

              errorHandler(DeclarationStore.TAG, error, 'sendDeclarationToAPIAsync', 'error');
              return false;
            }
          } else {
            return false;
          }
        }),
      );

      if (!okDeclarationSent.every((result: boolean) => result)) {
        return false;
      }

      // On envoie les pièces jointes
      // Prendre en compte l'envoi de pièce en asynchrone
      const attachmentPromiseResults = await this.sendAttachments(t);

      if (!attachmentPromiseResults) {
        return false;
      }

      // Mise a jour des consents
      const bConsentsTransferred = await this.RootStore.ConsentStore.updateToBoTransferredConsents();

      if (!bConsentsTransferred) return false;

      const {
        LoginStore: {
          signInInfos: { userUPN },
        },
      } = this.RootStore;

      // L'instrumentation est de la telemetrie: un echec d'envoi est rejoue plus tard et ne doit
      // pas faire echouer la synchronisation des declarations.
      await this.sendLogsToServerAsync(userUPN);

      // Envoi des suppressions d'inbox en attente (mises en file offline)
      await this.RootStore.InboxesStore.deleteQueuedInboxesAsync(tAxios);

      // Purge des inboxes de plus d'une journée.
      await inboxesDb.purgeInboxesAsync(userUPN);

      if (this.synchronizationStatus !== DeclarationSynchronizationStatus.NotStarted)
        this.setSynchronizationStatus(DeclarationSynchronizationStatus.Completed);
      return true;
    }

    return false;
  };

  sendAttachments = async (t: TFunction): Promise<boolean> => {
    // Envoi de toutes les photos en attente dans la BDD
    const toSend = await attachmentDb.getAttachmentToSend();
    this.logger.info(DeclarationStore.TAG, `ATTACHMENT_UPLOAD_START - count: ${toSend.length}`);
    this.setAttachmentsToSend(toSend.length);
    this.setSendingAttachments(toSend.length > 0);
    if (toSend.length > 0) {
      this.setSynchronizationStatus(DeclarationSynchronizationStatus.UploadingAttachments);
    } else {
      this.setSynchronizationStatus(DeclarationSynchronizationStatus.NotStarted);
    }

    // Envoi séquentiel : Safari iOS 18 cumule plusieurs régressions WebKit
    // (file de preflights CORS, blob backing store invalidé en parallèle) qui
    // font hanger un Promise.all sur N uploads multipart issus d'IndexedDB.
    // Un envoi un-par-un évite ces blocages.
    const sendOne = async (attachInput: QCAttachment): Promise<boolean> => {
      try {
        // Re-lecture fraîche depuis IDB juste avant l'upload : sur Safari iOS,
        // un File désérialisé peut être invalidé après un délai/contention.
        const fresh = await attachmentDb.getAttachmentById(attachInput.id);
        const attach = fresh ?? attachInput;
        this.logger.info(
          DeclarationStore.TAG,
          `ATTACHMENT_UPLOAD_PREPARE - id: ${attach.id} - source: ${fresh ? 'indexedDb' : 'input'} - uri: ${
            attach.distantUri
          } - fileType: ${Object.prototype.toString.call(attach.file)} - isFile: ${
            attach.file instanceof File
          } - name: ${attach.file?.name ?? 'undefined'} - size: ${attach.file?.size ?? 'undefined'} - type: ${
            attach.file?.type ?? 'undefined'
          }`,
        );
        if (!(attach.file instanceof File)) {
          const error = new Error(`Fichier introuvable ou invalide pour l'attachment ${attach.id}`);
          this.logger.error(DeclarationStore.TAG, `ATTACHMENT_UPLOAD_FILE_INVALID - ${error.message}`);
          errorHandler(DeclarationStore.TAG, error, 'sendAttachments', 'error');
          return false;
        }
        // Workaround for a WebKit issue where Files restored from IndexedDB
        // can be sent as an empty multipart body (Content-Length: 0)
        const buffer = await attach.file.arrayBuffer();

        const fileToSend = new File([buffer], attach.file.name, {
          type: attach.file.type,
          lastModified: attach.file.lastModified,
        });

        const data = new FormData();
        data.append('fileContent', fileToSend);

        const formDataEntries = Array.from(data.entries()).map(([key, value]) => ({
          key,
          valueType: value instanceof File ? 'File' : Object.prototype.toString.call(value),
          name: value instanceof File ? value.name : undefined,
          size: value instanceof File ? value.size : undefined,
          type: value instanceof File ? value.type : undefined,
          lastModified: value instanceof File ? value.lastModified : undefined,
        }));
        this.logger.log(DeclarationStore.TAG, `Envoi du fichier: ${attach.distantUri}`);
        // NB: on ne force PAS le Content-Type — axios doit le poser lui-même
        // pour inclure le boundary du multipart.
        this.logger.info(
          DeclarationStore.TAG,
          `ATTACHMENT_UPLOAD_REQUEST - id: ${attach.id} - uri: ${attach.distantUri} - contentType: ${
            attach.file.type
          } - formData: ${JSON.stringify(formDataEntries)} - userAgent: ${navigator.userAgent} - online: ${
            navigator.onLine
          }`,
        );
        const responseAttach = await this.clientHTTP.post(attach.distantUri, data, {
          withCredentials: true,
        });
        this.logger.info(
          DeclarationStore.TAG,
          `ATTACHMENT_UPLOAD_RESPONSE - id: ${attach.id} - status: ${responseAttach.status} - statusText: ${
            responseAttach.statusText
          } - contentType: ${responseAttach.headers?.['content-type'] ?? 'undefined'} - data: ${JSON.stringify(
            responseAttach.data,
          )}`,
        );
        if (200 <= responseAttach.status && responseAttach.status <= 299) {
          attach.transferState = DocumentTransferState.TransfertOK;
          this.logger.log(DeclarationStore.TAG, 'Fichier envoyé');
          this.decreaseAttachmentsToSend();
        } else {
          const error = new Error(
            `Fichier en erreur: ${attach.file.name} - Statut: ${responseAttach.status} - StatusText: ${responseAttach.statusText}`,
          );

          this.logger.error(
            DeclarationStore.TAG,
            `ATTACHMENT_UPLOAD_HTTP_FAILURE - id: ${attach.id} - uri: ${attach.distantUri} - status: ${
              responseAttach.status
            } - statusText: ${responseAttach.statusText} - response: ${JSON.stringify(responseAttach.data)}`,
          );
          errorHandler(DeclarationStore.TAG, error, 'sendAttachments', 'error');
          toast.error(t('errors_while_indexing_attachement', { attachement: attach.file.name }));
          attach.transferState = DocumentTransferState.TransfertKO;
          attach.nbTry = attach.nbTry - 1;
        }
        return await attachmentDb.updateAttachment(attach);
      } catch (error) {
        if (error instanceof AxiosError) {
          this.logger.error(
            DeclarationStore.TAG,
            `ATTACHMENT_UPLOAD_AXIOS_ERROR - id: ${attachInput.id} - uri: ${attachInput.distantUri} - code: ${
              error.code ?? 'undefined'
            } - message: ${error.message} - status: ${error.response?.status ?? 'undefined'} - statusText: ${
              error.response?.statusText ?? 'undefined'
            } - response: ${JSON.stringify(error.response?.data)}`,
          );
          errorHandler(DeclarationStore.TAG, error, 'sendAttachments', 'warn');
        } else {
          const errorMessage = error instanceof Error ? error.message : JSON.stringify(error);
          this.logger.error(
            DeclarationStore.TAG,
            `ATTACHMENT_UPLOAD_UNKNOWN_ERROR - id: ${attachInput.id} - uri: ${attachInput.distantUri} - message: ${errorMessage}`,
          );
          errorHandler(DeclarationStore.TAG, error, 'sendAttachments', 'error');
        }
        return false;
      }
    };

    try {
      const values: boolean[] = [];
      for (const attach of toSend) {
        values.push(await sendOne(attach));
      }

      await attachmentDb.removeAttachmentToPurge();

      if (!values.every((value) => value)) {
        this.setSendingAttachments(false);
        this.setSynchronizationStatus(DeclarationSynchronizationStatus.Failed);
        this.setSynchronizationError(t('qcapp_warning_attachments_send_failed', { ns: 'axios' }).toString());
        return false;
      }

      // Tempo (Demandé par Patrick)
      setTimeout(() => {
        this.setSendingAttachments(false);
      }, 2000);

      return true;
    } catch (reason: any) {
      errorHandler(DeclarationStore.TAG, reason, 'sendAttachments', 'warn');
      this.setSendingAttachments(false);
      this.setSynchronizationStatus(DeclarationSynchronizationStatus.Failed);
      return false;
    }
  };

  setInstrumentationFailureCount = (count: number): void => {
    this.instrumentationFailureCount = count;
  };

  /**
   * Prend en compte l'echec de l'envoi des logs d'instrumentation. Le compteur de tentatives est
   * incremente et l'erreur n'est tracee qu'au bout de trois echecs consecutifs: une coupure reseau
   * ponctuelle chez un utilisateur nomade n'est pas une anomalie a remonter en production. Les logs
   * restent en base locale et un rejeu differe est planifie.
   *
   * @param error L'erreur remontee par l'envoi.
   */
  private handleInstrumentationFailure = (error: unknown): void => {
    const attempt = this.instrumentationFailureCount + 1;
    const message = error instanceof Error ? error.message : String(error);

    if (attempt >= DeclarationStore.MAX_INSTRUMENTATION_ATTEMPTS) {
      // Troisieme echec consecutif: l'anomalie est averee, on la trace et le compteur repart a zero.
      this.setInstrumentationFailureCount(0);
      this.logger.error(
        DeclarationStore.TAG,
        `sendLogsToServerAsync: echec de l'envoi des logs d'instrumentation apres ${DeclarationStore.MAX_INSTRUMENTATION_ATTEMPTS} tentatives - Erreur: ${message}`,
      );
    } else {
      this.setInstrumentationFailureCount(attempt);
      this.logger.debug(
        DeclarationStore.TAG,
        `sendLogsToServerAsync: echec de l'envoi des logs d'instrumentation (tentative ${attempt}/${DeclarationStore.MAX_INSTRUMENTATION_ATTEMPTS}), nouvel essai differe - Erreur: ${message}`,
      );
    }

    this.scheduleInstrumentationRetry();
  };

  /**
   * Planifie un nouvel envoi des logs d'instrumentation. Le rejeu est volontairement differe pour ne
   * pas perturber l'utilisateur: s'il survient pendant la saisie d'une declaration, pendant une
   * synchronisation ou hors connexion, il est simplement replanifie. Le retour de la connexion
   * reseau declenche par ailleurs une tentative immediate.
   */
  private scheduleInstrumentationRetry = (): void => {
    if (this.instrumentationRetryTimeoutId === undefined) {
      this.instrumentationRetryTimeoutId = setTimeout(() => {
        this.instrumentationRetryTimeoutId = undefined;
        void this.retryInstrumentationSendAsync();
      }, DeclarationStore.INSTRUMENTATION_RETRY_DELAY_IN_MS);
    }

    if (this.onNetworkBackOnline === undefined) {
      this.onNetworkBackOnline = () => {
        void this.retryInstrumentationSendAsync();
      };

      window.addEventListener('online', this.onNetworkBackOnline);
    }
  };

  /**
   * Annule le rejeu differe en attente et retire l'ecouteur de retour de connexion.
   */
  private cancelInstrumentationRetry = (): void => {
    if (this.instrumentationRetryTimeoutId !== undefined) {
      clearTimeout(this.instrumentationRetryTimeoutId);
      this.instrumentationRetryTimeoutId = undefined;
    }

    if (this.onNetworkBackOnline !== undefined) {
      window.removeEventListener('online', this.onNetworkBackOnline);
      this.onNetworkBackOnline = undefined;
    }
  };

  /**
   * Rejoue l'envoi des logs d'instrumentation si le contexte s'y prete. Dans le cas contraire
   * (utilisateur non connecte, saisie ou synchronisation en cours, absence de reseau), la tentative
   * est replanifiee sans rien envoyer.
   */
  private retryInstrumentationSendAsync = async (): Promise<void> => {
    const {
      LoginStore: {
        signInInfos: { userUPN },
      },
    } = this.RootStore;

    const isBusy =
      this.hasUnsavedChanges ||
      this.sendingDeclaration ||
      this.sendingAttachments ||
      DeclarationStore.SYNCHRONIZATION_IN_PROGRESS_STATUSES.includes(this.synchronizationStatus);

    if (!userUPN || isBusy || navigator.onLine === false) {
      this.scheduleInstrumentationRetry();

      return;
    }

    await this.sendLogsToServerAsync(userUPN);
  };

  /**
   * Permet d'envoyer les logs à l'API via le endPoint "saveintrumentation".
   * Un echec n'interrompt jamais le flux appelant: les logs restent en base locale, le compteur de
   * tentatives est incremente et l'envoi est rejoue plus tard (rejeu differe, prochaine declaration
   * ou prochaine deconnexion).
   *
   * @param {string} userUPN Identifiant de l'utilisateur
   * @returns True si les logs ont ete transmis
   */
  sendLogsToServerAsync = async (userUPN: string): Promise<boolean> => {
    // Un seul envoi a la fois: synchronisation, deconnexion et rejeu differe peuvent se croiser.
    if (this.isSendingInstrumentationLogs) return false;

    this.isSendingInstrumentationLogs = true;

    try {
      // Récupération de tous les logs exceptés les logs errors dans la table indexedDB

      const telemetrylogDB = await logDb.getLogs(this.logger);

      if (telemetrylogDB) {
        // Construction de la requête
        const IMEI = getSessionId();
        const ApplicationName = APP_NAME;
        const { CrashDump, Items } = telemetrylogDB;
        const telemetryLogsRequest: TelemetryLogsRequest = {
          userUPN,
          IMEI,
          ApplicationName,
          CrashDump,
          Items,
        };

        let logUrl: string | undefined;
        let logToken: string | undefined;

        const {
          LoginStore: { urlToUse },
        } = this.RootStore;

        const {
          REACT_APP_ENV,
          REACT_APP_STORAGE_URL_DEFAULT,
          REACT_APP_STORAGE_TOKEN_DEFAULT,
          REACT_APP_STORAGE_URL_COLASDS,
          REACT_APP_STORAGE_TOKEN_COLASDS,
          REACT_APP_STORAGE_URL_VSL,
          REACT_APP_STORAGE_TOKEN_VSL,
        } = process.env;

        switch (REACT_APP_ENV) {
          case 'dev':
          case 'int':
          case 'demo':
            logUrl = REACT_APP_STORAGE_URL_DEFAULT;
            logToken = REACT_APP_STORAGE_TOKEN_DEFAULT;
            break;
          case 'prod':
          default:
            if (urlToUse?.startsWith('https://colasds.api.')) {
              logUrl = REACT_APP_STORAGE_URL_COLASDS;
              logToken = REACT_APP_STORAGE_TOKEN_COLASDS;
            } else if (urlToUse?.startsWith('https://vsl.api.')) {
              logUrl = REACT_APP_STORAGE_URL_VSL;
              logToken = REACT_APP_STORAGE_TOKEN_VSL;
            } else {
              logUrl = REACT_APP_STORAGE_URL_DEFAULT;
              logToken = REACT_APP_STORAGE_TOKEN_DEFAULT;
            }
            break;
        }

        // Rien a transmettre: inutile de solliciter le stockage.
        if (Items.length === 0 && (CrashDump === null || CrashDump.length === 0)) return true;

        const guid = uuidv4();
        const url = `${logUrl}/saveinstrumentationrequest_${guid}.qcjob_instrum?${logToken}`;

        // L'echec est gere ici (compteur de tentatives et rejeu differe): le client HTTP ne doit ni
        // tracer d'erreur ni declencher ses effets de bord generiques.
        const instrumentationConfig: QcRequestConfig = {
          headers: {
            'x-ms-blob-type': 'BlockBlob',
            'x-ms-blob-content-type': 'application/json',
          },
          silentOnError: true,
        };

        // Récupération de la response
        const response = await this.clientHTTP.put<SaveInstrumentationResponse>(
          url,
          telemetryLogsRequest,
          instrumentationConfig,
        );

        if (response.status < 200 || response.status >= 300) {
          this.handleInstrumentationFailure(
            new Error(
              `La requête de l'instrumentation dans la function sendLogsToServerAsync dans le declarationStore a échoué: Statut: ${response.data.status}, Message: ${response.data.message}`,
            ),
          );

          return false;
        }

        // Envoi reussi: le compteur repart de zero et le rejeu en attente devient inutile.
        this.setInstrumentationFailureCount(0);
        this.cancelInstrumentationRetry();

        // Destruction des logs envoyés
        await logDb.deleteLogs(telemetrylogDB.ids);
        return true;
      } else {
        return true;
      }
    } catch (error) {
      this.handleInstrumentationFailure(error);

      return false;
    } finally {
      this.isSendingInstrumentationLogs = false;
    }
  };

  private async getEditedData(
    declarationId: string,
    {
      formId,
      name,
      formParentId,
      formBody,
      entitySchemaId,
      formType,
      entityInstanceId,
      templateBodies,
      editedAt,
      validationAction,
    }: DeclarationContext,
    userUpn: string,
    declaration: FieldDesc[],
    attachment: AttachmentItemData[],
    organizationalUnitName: string,
    languageCode: string,
    itemData: ItemData | undefined,
    activityId: string | undefined,
    inboxId: string | undefined,
    isDraft: boolean,
    sendAPI: boolean | undefined,
    t: TFunction,
    stringExtract?: string,
  ): Promise<FormEditedData> {
    const attachData = new Map<string, AttachmentItemData>();
    attachment.map((attach) => {
      delete attach.file;
      attachData.set(attach.id, attach);
    });

    // Utilisation du ComputeStore en passant par le RootStore
    const {
      LoginStore: { currentDeclarationOuCode },
      ComputeStore: { formatValuesBeforeSendDeclaration },
      EquipmentsStore: { isEmptyDataSourceForcedSelection },
    } = this.RootStore;

    // Cas QCAction si la valeur est vide
    const emptyActionsIds = flatten(declaration, (i) => i.items)
      .filter((f) => {
        const emptyValue = (Array.isArray(f.value) && f.value.length === 0) || !!f.value === false;
        return f.fieldType === FieldType.Action && emptyValue;
      })
      .map(({ id }: FieldDesc) => id);

    let simpleFields = declaration
      .map((f) => formatValuesBeforeSendDeclaration(f))
      .map(isEmptyDataSourceForcedSelection)
      .filter((f) => mapToFieldVisibility(f))
      .map((f) => mapToFieldData(f));

    notificationFieldsCheckUnprocessedData(simpleFields, isDraft, sendAPI, this.setFieldFocused, t);

    if (formType === FormType.Workflow) {
      const allAttachments = await attachmentDb.getAttachmentItemDataByDeclarationId(declarationId);

      if (allAttachments.length > 0) {
        simpleFields = mapDistantUriForOtherStep(allAttachments, simpleFields);
      }
    }
    // cas inbox : pour supprimer l'inbox apres l'envoi de declaration
    let isInbox;
    if (formType !== FormType.Workflow) {
      const isTodoList = simpleFields.filter((item) => item.fieldType === FieldType.TodoList);
      isInbox = inboxId
        ? { Inbox: { Id: inboxId, Type: isTodoList.length ? InboxItemType.Task : InboxItemType.PrefilledForm } }
        : itemData;
    }
    if (formType === FormType.Workflow) {
      delete itemData?.PrefilledData;
      delete itemData?.TodoListData;
      if (inboxId) {
        const x = itemData?.Workflow?.historicalStepDataList?.find(
          (item) => item.stepState === WorkflowStepStatus.Canceled,
        );
        isInbox = x ? { ...itemData, Inbox: { Id: inboxId, Type: InboxItemType.PrefilledForm } } : itemData;
      }
    } else if (itemData && inboxId) {
      isInbox = { ...itemData, Inbox: { Id: inboxId, Type: InboxItemType.PrefilledForm } };
    } else {
      isInbox = itemData;
    }

    return {
      id: declarationId,
      eTag: '',
      formParentId: formParentId,
      formId: formId,
      name: name,
      serializedBody: formBody,
      templateBodies: templateBodies ? JSON.stringify(templateBodies) : null,
      userUpn,
      attachmentData: JSON.stringify(Array.from(attachData.entries())),
      createdAt: editedAt ?? new Date(),
      editedAt: editedAt ?? new Date(),
      editedData: JSONBig.stringify(simpleFields),
      // stringExtract reste original
      stringExtract: stringExtract ?? name,
      entitySchemaId: entitySchemaId,
      entityInstanceId: entityInstanceId,
      alertLevelValue: '',
      alertLevelLabel: '',
      workflowStatus: undefined, // ne sert à rien
      entityInstanceStringExtract: '',
      organizationalUnitStringExtract: organizationalUnitName,
      formType: formType,
      itemData: JSON.stringify(isInbox ?? itemData) === '{}' ? null : JSON.stringify(isInbox ?? itemData),
      itemDataStatus: itemData?.Workflow?.currentWorkflowState ?? 0,
      itemDataComment: '',
      activityId: activityId ?? uuidv4(),
      parentActivityId: undefined,
      languageCode: languageCode,
      inboxId: inboxId,
      isDraft: isDraft,
      // stringExtractDisplayed est celui affiché
      stringExtractDisplayed: stringExtract,
      internalData: this.internalData.length === 0 ? undefined : JSON.stringify(this.internalData),
      validationAction: validationAction ?? ValidationActionType.Validated,
      ouCode: currentDeclarationOuCode(),
    } as FormEditedData;
  }

  async getOldDeclaration(requestInfo: RequestData) {
    try {
      const result = await this.clientHTTP.post(
        this.RootStore.CommonStore.chooseBaseUrl(API_POST_HISTORIQUEDECLARATION),
        requestInfo,
        {
          withCredentials: true,
        },
      );
      if (result) {
        return result;
      }
    } catch (error) {
      errorHandler(DeclarationStore.TAG, error, 'getOldDeclaration', 'error');
    }
  }

  async actionSearchData(requestData?: SearchRequestData) {
    try {
      const result = await this.clientHTTP.post<ActionResponse>(
        this.RootStore.CommonStore.chooseBaseUrl(API_POST_QC_ACTION),
        requestData,
        {
          withCredentials: true,
        },
      );
      if (result) {
        return result;
      }
    } catch (error) {
      if (QCErrorHTTP.isHttpError<AxiosError<ActionResponse>>(error)) {
        error.message = `Statut: ${error.response?.data.status} - Message: ${error.response?.data.message}`;
        toast.warn(error.response?.data.message);
      }
      errorHandler(DeclarationStore.TAG, error, 'actionSearchData');
    }
  }

  /**
   *Permet de récupérer les saisies interrompues de l'utilisateur - Celles qui ne sont ouvertes dans aucun onglet sont proposées à la reprise dans une popup
   *
   * @param {string} userUPN
   * @memberof DeclarationStore
   */
  findBackUpsByUserUPNAsync = async (userUPN: string): Promise<void> => {
    try {
      const backups = await declarationsDb.findBackUpByUserUPNAsync(this.logger, userUPN);
      if (backups.length === 0) return;

      const heldLockNames = await getHeldEditLockNamesAsync();
      this.setBackupDclContext(
        backups.filter(({ id }: LocalBackupDeclaration) => !!id && !heldLockNames.has(getDeclarationEditLockName(id))),
      );
    } catch (error: unknown) {
      errorHandler(DeclarationStore.TAG, error, 'findBackUpsByUserUPNAsync');
    }
  };

  /**
   * Abandonne une saisie interrompue proposée à la reprise. Elle n'est plus proposée, même si elle a été ouverte entre-temps
   * dans un autre onglet : elle n'est alors pas supprimée et false est retourné.
   */
  discardBackupDeclarationAsync = async (declarationId: string): Promise<boolean> => {
    this.removeFromBackupDclContext(declarationId);

    return runWithEditLockAsync(getDeclarationEditLockName(declarationId), () => this.removeBackUpDcl(declarationId));
  };

  /** Une saisie de ce formulaire est en cours dans un autre onglet */
  isFormEditedElsewhereAsync = async (formId: string): Promise<boolean> =>
    (await getHeldEditLockNamesAsync()).has(getFormEditLockName(formId));

  /**
   * Supprime un brouillon depuis une liste. Retourne false, sans le supprimer, s'il est ouvert dans un onglet.
   */
  deleteDraftDeclarationAsync = async (declarationId: string): Promise<boolean> =>
    runWithEditLockAsync(getDeclarationEditLockName(declarationId), () =>
      this.removeLocalDeclaration(declarationId, true),
    );

  private decreaseAttachmentsToSend = (): void => {
    const newValue = this.attachmentsToSend - 1;
    this.setAttachmentsToSend(newValue);
  };

  public parseInternalData = (serializedInternalData: string): void | never => {
    if (typeof serializedInternalData !== 'string' || serializedInternalData.startsWith('[') === false)
      throw new Error(
        '[Client Web] declarationStore.ts parseInternalData method failed: La donnée serializedInternalData doit être une chaine de caractères sérializée',
      );

    const internalDataParsed: FieldData[] = JSON.parse(serializedInternalData);

    if (!Array.isArray(internalDataParsed)) return;

    internalDataParsed.forEach((fd: FieldData) => {
      this.internalDataDico.set(fd.id, fd);
    });
  };

  /**
   * Enregistre l'état du champ dans le cas des internalData
   *
   * @param {string} fullId
   * @param {InternalFieldState} state
   * @param {boolean} value
   * @memberof DeclarationStore
   */
  public registerNewState = (fullId: string, state: InternalFieldState, value: boolean) => {
    const dicoKey = `${fullId}.(state)`;
    const fd: FieldData | undefined = this.internalDataDico.get(dicoKey);

    if (!fd) {
      const sfd: FieldData = {
        id: dicoKey,
        label: 'state',
        fieldType: FieldType.Text,
        value: this.getStateValue(state, value, '----'),
      };

      this.internalDataDico.set(dicoKey, sfd);
    } else {
      const { value: previousValue } = fd;
      this.internalDataDico.set(dicoKey, { ...fd, value: this.getStateValue(state, value, previousValue as string) });
    }
  };

  private getStateValue = (
    state: InternalFieldState,
    stateValue: boolean | null,
    currentStateValue: string | null,
  ): string => {
    if (currentStateValue === null || currentStateValue.length !== 4) return '----';

    const sStateValue: string = stateValue === null ? '-' : stateValue ? '1' : '0';

    switch (state) {
      case InternalFieldState.Visible:
        return StringExtension.replaceAt(0, sStateValue, currentStateValue);

      case InternalFieldState.ReadOnly:
        return StringExtension.replaceAt(1, sStateValue, currentStateValue);

      case InternalFieldState.Mandatory:
        return StringExtension.replaceAt(2, sStateValue, currentStateValue);

      case InternalFieldState.Valid:
        return StringExtension.replaceAt(3, sStateValue, currentStateValue);
      case InternalFieldState.Undef:
      default:
        return '---';
    }
  };

  public registerNewDataSource = (
    fullId: string,
    { code, instanceId, schemaId, title, asFieldDataList }: DataSourceValue,
  ) => {
    const dicoKey = `${fullId}.(ds)`;
    const fd: FieldData | undefined = this.internalDataDico.get(dicoKey);

    if (!fd) {
      const fdl: FieldData[] = asFieldDataList();

      const dsFd: FieldData = {
        id: dicoKey,
        label: 'ds',
        fieldType: FieldType.DataSource,
        code,
        instanceId,
        schemaId,
        title,
        value: fdl,
      };

      this.internalDataDico.set(dicoKey, dsFd);
    } else {
      this.internalDataDico.set(dicoKey, { ...fd, value: asFieldDataList() });
    }
  };

  public registerNewAutoCompleteForFamilly = (fullId: string, schemaId: string, entityInstanceId: string) => {
    // (AutoCompletSchema
    const dicoKey = `${fullId}.(acs)`;
    const fd: FieldData | undefined = this.internalDataDico.get(dicoKey);

    // On enregistre la nouvelle valeur du champ autoComplete de la manière suivante:
    // SchemaId/EntityInstanceId

    const value = `${schemaId}/${entityInstanceId}`;

    if (!fd) {
      // On crée le nouveau StringFieldData
      const dsFd: FieldData = {
        id: dicoKey,
        label: 'acs',
        fieldType: FieldType.Text,
        value,
      };

      this.internalDataDico.set(dicoKey, dsFd);
    } else {
      this.internalDataDico.set(dicoKey, { ...fd, value });
    }
  };

  public registerNewAutoCompleteForAAD = (fullId: string, value: string) => {
    // (AutoCompletSchema
    const dicoKey = `${fullId}.(acc)`;
    const fd: FieldData | undefined = this.internalDataDico.get(dicoKey);

    // On enregistre la nouvelle valeur du champ autoComplete de la manière suivante:

    if (!fd) {
      // On crée le nouveau StringFieldData
      const dsFd: FieldData = {
        id: dicoKey,
        label: 'acc',
        fieldType: FieldType.Text,
        value,
      };

      this.internalDataDico.set(dicoKey, dsFd);
    } else {
      this.internalDataDico.set(dicoKey, { ...fd, value });
    }
  };

  public deleteAutoCompleteForFamilly = (fullId: string) => {
    // (AutoCompletSchema
    const dicoKey = `${fullId}.(acs)`;
    const isExist: boolean = this.internalDataDico.has(dicoKey);

    if (!isExist) return;

    this.internalDataDico.delete(dicoKey);
  };

  public deleteAutoCompleteForAAD = (fullId: string) => {
    // (AutoCompletSchema
    const dicoKey = `${fullId}.(acc)`;
    const isExist: boolean = this.internalDataDico.has(dicoKey);

    if (!isExist) return;

    this.internalDataDico.delete(dicoKey);
  };

  async hasDraftsDcl(userUPN: string): Promise<boolean> {
    return (await declarationsDb.getLocalDrafts(userUPN, this.logger)).length > 0;
  }

  /** Supprime la sauvegarde automatique d'une déclaration, qui n'est alors plus proposée à la reprise */
  async removeBackUpDcl(declarationId: string): Promise<boolean> {
    this.removeFromBackupDclContext(declarationId);
    return !!(await declarationsDb.removeBackupDeclaration(this.logger, declarationId));
  }

  /** La saisie interrompue n'est plus proposée à la reprise */
  removeFromBackupDclContext = (declarationId: string): void => {
    this.setBackupDclContext(this.backupDclContext.filter(({ id }: LocalBackupDeclaration) => id !== declarationId));
  };

  /**
   * Récupère les données du LRU en fonction de l'ID du champ et du nombre indiqué par l'attribut LRUCount
   * @param fullId
   * @param lruCount
   */
  async getLRUDescriptionsByFieldId(fields: FieldDesc[], fullId: string, lruCount: number): Promise<string[]> {
    try {
      const {
        LoginStore: {
          signInInfos: { userUPN },
        },
      } = this.RootStore;

      const lruFullId = getLRUFullId(fields, fullId.split('.'));
      const result = await fieldSearchHistoryDb.getLRUDescriptionsByFieldLRUFullIdAsync(lruFullId, userUPN);

      if (!result) return [];

      return result.description.reverse().slice(0, lruCount);
    } catch (error) {
      errorHandler(DeclarationStore.TAG, error, 'getLRUDescriptionsByFieldId');
      return [];
    }
  }

  async modifyFieldHistory(objectToModify: IFieldSearchHistory) {
    await fieldSearchHistoryDb.modifyFieldHistory(objectToModify);
  }

  async deleteLRUValue(fields: FieldDesc[], fullId: string, userUPN: string, value: string): Promise<void> {
    try {
      const lruFullId = getLRUFullId(fields, fullId.split('.'));

      await fieldSearchHistoryDb.removeLRUValue(lruFullId, userUPN, value);
    } catch (error) {
      errorHandler(DeclarationStore.TAG, error, 'deleteLRUValue');
    }
  }
}

export default DeclarationStore;
